import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, WheelEvent } from 'react';
import { Link } from 'react-router';
import { ManagementViewKpis } from '../../../shared/types/managementViewKpi.ts';
import { CncEquipment } from '../../../shared/types/mtlinkiTypes.ts';
import { sortBy } from '../../../shared/utils/sortUtils.ts';
import { NavBar } from '../../components/navBar.tsx';
import { ModalDialog } from '../../components/modalDialog.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';
import { convertToCsv } from '../../utils/csv.ts';
import { saveFileWithPicker } from '../../utils/saveFile.ts';
import { stringToBlob } from '../../utils/stringToBlob.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import {
    createManagementViewExportTable,
    getAvailableManagementViewColumns,
    getManagementViewLoadingProgress,
    getVisibleManagementViewColumns,
    MANAGEMENT_VIEW_COLUMN_IDS,
    MANAGEMENT_VIEW_DEFAULT_COLUMN_IDS,
    ManagementViewColumn,
    ManagementViewColumnId,
    ManagementViewRow,
    ManagementViewSort,
    normalizeManagementViewColumnIds,
    sortManagementViewRows,
} from './managementViewTable.ts';
import { PriceDialog } from './priceDialog.tsx';


const VISIBLE_COLUMNS_STORAGE_KEY = 'managementViewVisibleColumns';

export function ManagementView() {
    const [priceDialogKpis, setPriceDialogData] = useState<ManagementViewKpis | null>(null);

    const { data: isAdmin, isLoading: isAdminLoading } = trpc.user.isAdmin.useQuery(
        undefined,
        { refetchOnWindowFocus: false }
    );

    const { data: equipments, isLoading: equipmentsLoading } = trpc.equipments.getAllCnc.useQuery(
        undefined,
        { select: (equipments) => sortBy([...equipments], it => it.displayName) }
    );

    const equipmentKpis = trpc.useQueries((t) =>
        equipments?.map(equipment =>
            t.managementView.getEquipmentKpis(
                { equipment: equipment.id },
                { refetchOnWindowFocus: false, }
            )
        ) ?? []
    );

    const handlePriceClicked = (kpis: ManagementViewKpis | undefined) => {
        if (!kpis || !kpis.product) return;
        if (!kpis.partName) {
            alert('Part number has not been set for this program.');
            return;
        }
        setPriceDialogData(kpis);
    }

    const handlePriceChanged = () => {
        equipmentKpis
            .filter(it => it.data?.product === priceDialogKpis?.product)
            .forEach(it => it.refetch());
    }

    return (
        <>
            <PageTitle title='Management View' />
            <NavBar />
            <div className='content management-view__content'>
                {(equipmentsLoading || isAdminLoading) &&
                    <ManagementViewLoadingBar />
                }
                {(equipments && isAdmin != null) &&
                    <ManagementViewTable
                        equipments={equipments}
                        equipmentKpis={equipmentKpis}
                        isAdmin={isAdmin}
                        onPriceClicked={handlePriceClicked}
                    />
                }
            </div>

            {priceDialogKpis &&
                <PriceDialog
                    managementViewKpis={priceDialogKpis}
                    onClose={() => setPriceDialogData(null)}
                    onPriceSubmitted={handlePriceChanged}
                />
            }
        </>
    );
}

interface ManagementViewTableProps {
    equipments: CncEquipment[];
    equipmentKpis: UseQueryResult<ManagementViewKpis, Error>[];
    isAdmin: boolean;
    onPriceClicked: (kpis: ManagementViewKpis | undefined) => void;
}

function ManagementViewTable({
    equipments,
    equipmentKpis,
    isAdmin,
    onPriceClicked,
}: ManagementViewTableProps) {
    const [visibleColumnIds, setVisibleColumnIds] = useState<ManagementViewColumnId[]>(
        loadVisibleColumnIds
    );
    const [isColumnsDialogOpen, setIsColumnsDialogOpen] = useState(false);
    const columnOptionsRef = useRef<HTMLDivElement>(null);
    const [sort, setSort] = useState<ManagementViewSort>({
        columnId: 'equipment',
        direction: 'asc',
    });

    const availableColumns = useMemo(
        () => getAvailableManagementViewColumns(isAdmin),
        [isAdmin],
    );
    const visibleColumns = useMemo(
        () => getVisibleManagementViewColumns(visibleColumnIds, isAdmin),
        [visibleColumnIds, isAdmin],
    );
    const rows = useMemo<ManagementViewRow[]>(
        () => equipments.map((equipment, index) => ({
            equipmentId: equipment.id,
            equipmentName: equipment.displayName,
            isLoading: equipmentKpis[index].isLoading,
            kpis: equipmentKpis[index].data,
        })),
        [equipments, equipmentKpis],
    );
    const sortedRows = useMemo(
        () => sortManagementViewRows(rows, sort),
        [rows, sort],
    );
    const loadingProgress = useMemo(
        () => getManagementViewLoadingProgress(rows),
        [rows],
    );

    const setVisibleColumnIdsAndSave = (columnIds: ManagementViewColumnId[]) => {
        const normalizedColumnIds = normalizeManagementViewColumnIds(columnIds);
        setVisibleColumnIds(normalizedColumnIds);
        localStorage.setItem(
            VISIBLE_COLUMNS_STORAGE_KEY,
            JSON.stringify(normalizedColumnIds),
        );
    }

    const handleSort = (columnId: ManagementViewColumnId) => {
        setSort(current => ({
            columnId,
            direction: current.columnId === columnId && current.direction === 'asc'
                ? 'desc'
                : 'asc',
        }));
    }

    const handleColumnVisibilityChange = (
        columnId: ManagementViewColumnId,
        isVisible: boolean,
    ) => {
        if (columnId === 'equipment') return;

        const nextIds = MANAGEMENT_VIEW_COLUMN_IDS.filter(id => {
            if (id === columnId) return isVisible;
            return visibleColumnIds.includes(id);
        });
        setVisibleColumnIdsAndSave(nextIds);
        if (!isVisible && sort.columnId === columnId) {
            const nextSortColumnId = nextIds
                .find(id => availableColumns.some(column => column.id === id));
            if (nextSortColumnId) {
                setSort({ columnId: nextSortColumnId, direction: 'asc' });
            }
        }
    }

    const handleExport = async () => {
        const table = createManagementViewExportTable(sortedRows, visibleColumns);
        const csvText = convertToCsv(table, { delimiter: ';' });
        const csvBlob = stringToBlob(csvText, 'csv');
        const now = new Date();
        const date = [
            now.getFullYear(),
            (now.getMonth() + 1).toString().padStart(2, '0'),
            now.getDate().toString().padStart(2, '0'),
        ].join('-');
        await saveFileWithPicker(`management-view-${date}.csv`, csvBlob, 'csv');
    }

    const isExportDisabled = sortedRows.length === 0 || sortedRows.some(row => row.isLoading);

    const handleColumnsDialogWheel = (event: WheelEvent<HTMLDivElement>) => {
        const columnOptions = columnOptionsRef.current;
        if (!columnOptions || columnOptions.contains(event.target as Node)) return;

        event.preventDefault();
        columnOptions.scrollBy({ top: event.deltaY });
    }

    return (
        <div className='management-table-container'>
            <div className='management-table__settings-bar'>
                <div className='management-table__toolbar'>
                    <button type='button' onClick={() => setIsColumnsDialogOpen(true)}>
                        Columns
                    </button>
                    <button type='button' disabled={isExportDisabled} onClick={handleExport}>
                        Export CSV
                    </button>
                    <ManagementViewLoadingBar
                        completed={loadingProgress.completed}
                        total={loadingProgress.total}
                        percentage={loadingProgress.percentage}
                        isInToolbar={true}
                        isActive={loadingProgress.isLoading}
                    />
                </div>
            </div>
            <ModalDialog
                isOpen={isColumnsDialogOpen}
                backgroundClassName='modal-background--blur'
                frameClassName='management-table__columns-dialog'
                contentClassName='management-table__columns-dialog-layout'
                cancelOnOutsideClick={true}
                lockBackgroundScroll={true}
                onBackgroundWheel={handleColumnsDialogWheel}
                onCancel={() => setIsColumnsDialogOpen(false)}
                showCloseX={false}
            >
                <div className='management-table__columns-dialog-header'>
                    <div>
                        <h2>Choose columns</h2>
                        <p>Select the information shown in the management table.</p>
                    </div>
                    <button
                        type='button'
                        className='management-table__columns-dialog-close'
                        aria-label='Close column selection'
                        onClick={() => setIsColumnsDialogOpen(false)}
                    >
                        <span aria-hidden='true'>&times;</span>
                    </button>
                </div>
                <div className='management-table__columns-dialog-content'>
                    <div
                        ref={columnOptionsRef}
                        className='management-table__column-options'
                    >
                        {availableColumns.map(column => {
                            const isVisible = visibleColumnIds.includes(column.id);
                            const isRequired = column.id === 'equipment';
                            return (
                                <label
                                    key={column.id}
                                    className={isRequired
                                        ? 'management-table__column-option management-table__column-option--required'
                                        : 'management-table__column-option'}
                                >
                                    <input
                                        type='checkbox'
                                        checked={isVisible}
                                        disabled={isRequired}
                                        onChange={event => handleColumnVisibilityChange(
                                            column.id,
                                            event.target.checked,
                                        )}
                                    />
                                    <span>{column.menuLabel ?? column.label}</span>
                                    {isRequired && (
                                        <span className='management-table__column-required'>
                                            Required
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                    <div className='management-table__columns-dialog-actions'>
                        <span className='management-table__columns-selected-count'>
                            {visibleColumns.length} of {availableColumns.length} selected
                        </span>
                        <button
                            type='button'
                            className='management-table__columns-default-button'
                            onClick={() => setVisibleColumnIdsAndSave(
                                MANAGEMENT_VIEW_DEFAULT_COLUMN_IDS
                            )}
                        >
                            Default
                        </button>
                        <button
                            type='button'
                            className='management-table__columns-done-button'
                            onClick={() => setIsColumnsDialogOpen(false)}
                        >
                            Done
                        </button>
                    </div>
                </div>
            </ModalDialog>
            <div className='management-table__scroll'>
                <table className='management-table'>
                    <thead>
                        <tr>
                            {visibleColumns.map(column =>
                                <ManagementViewHeader
                                    key={column.id}
                                    column={column}
                                    sort={sort}
                                    onSort={handleSort}
                                />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map(row =>
                            <tr key={row.equipmentId}>
                                {visibleColumns.map(column =>
                                    <ManagementViewCell
                                        key={column.id}
                                        column={column}
                                        row={row}
                                        onPriceClicked={onPriceClicked}
                                    />
                                )}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

interface ManagementViewLoadingBarProps {
    completed?: number;
    total?: number;
    percentage?: number;
    isInToolbar?: boolean;
    isActive?: boolean;
}

type ManagementViewLoadingPhase = 'loading' | 'completing' | 'fading' | 'hidden';

function ManagementViewLoadingBar({
    completed,
    total,
    percentage,
    isInToolbar = false,
    isActive = true,
}: ManagementViewLoadingBarProps) {
    const [phase, setPhase] = useState<ManagementViewLoadingPhase>(
        isActive ? 'loading' : 'hidden'
    );
    const wasActiveRef = useRef(isActive);
    const isDeterminate = completed != null && total != null && percentage != null;
    const showIndeterminateMotion = !isDeterminate || completed === 0;
    const progressText = isDeterminate ? ` (${completed} of ${total})` : '';

    useEffect(() => {
        if (isActive) {
            wasActiveRef.current = true;
            setPhase('loading');
            return;
        }

        if (!wasActiveRef.current) {
            setPhase('hidden');
            return;
        }

        wasActiveRef.current = false;
        setPhase('completing');
        const fadeTimer = window.setTimeout(() => setPhase('fading'), 850);
        const hideTimer = window.setTimeout(() => setPhase('hidden'), 1750);

        return () => {
            window.clearTimeout(fadeTimer);
            window.clearTimeout(hideTimer);
        };
    }, [isActive]);

    const isVisible = phase !== 'hidden';

    return (
        <div
            className={[
                'management-table__loading',
                isInToolbar ? 'management-table__loading--toolbar' : '',
                `management-table__loading--${phase}`,
            ].filter(Boolean).join(' ')}
            role={isVisible ? 'status' : undefined}
            aria-live='polite'
            aria-hidden={!isVisible}
        >
            <div className='management-table__loading-label'>
                Loading management data{progressText}
            </div>
            <div
                className='management-table__loading-track'
                role='progressbar'
                aria-label='Loading management data'
                aria-valuemin={isDeterminate ? 0 : undefined}
                aria-valuemax={isDeterminate ? total : undefined}
                aria-valuenow={isDeterminate ? completed : undefined}
            >
                <div
                    className={showIndeterminateMotion && phase === 'loading'
                        ? 'management-table__loading-value management-table__loading-value--indeterminate'
                        : 'management-table__loading-value'}
                    style={!showIndeterminateMotion || phase !== 'loading'
                        ? { width: phase === 'loading' ? `${percentage}%` : '100%' }
                        : undefined}
                />
            </div>
        </div>
    );
}

interface ManagementViewHeaderProps {
    column: ManagementViewColumn;
    sort: ManagementViewSort;
    onSort: (columnId: ManagementViewColumnId) => void;
}

function ManagementViewHeader({
    column,
    sort,
    onSort,
}: ManagementViewHeaderProps) {
    const isSorted = sort.columnId === column.id;
    const ariaSort = isSorted
        ? sort.direction === 'asc' ? 'ascending' : 'descending'
        : 'none';

    return (
        <th
            style={column.headerStyle}
            title={column.title}
            aria-sort={ariaSort}
        >
            <button
                type='button'
                className='table-sort-button'
                onClick={() => onSort(column.id)}
            >
                <span>{column.label}</span>
                {isSorted &&
                    <span
                        className={`table-sort-indicator table-sort-indicator--${sort.direction}`}
                        aria-hidden='true'
                    />
                }
            </button>
        </th>
    );
}

interface ManagementViewCellProps {
    column: ManagementViewColumn;
    row: ManagementViewRow;
    onPriceClicked: (kpis: ManagementViewKpis | undefined) => void;
}

function ManagementViewCell({
    column,
    row,
    onPriceClicked,
}: ManagementViewCellProps) {
    if (column.id === 'equipment') {
        return (
            <td className={column.cellClassName}>
                <Link
                    className='link-button--non-intrusive'
                    to={`/machine-log#equipment=${row.equipmentId}`}
                >
                    {row.equipmentName}
                </Link>
            </td>
        );
    }

    const isPriceCell = column.id === 'unitPrice';
    return (
        <td
            className={column.cellClassName}
            title={column.id === 'partNumber' ? row.kpis?.product : undefined}
            onClick={isPriceCell && !row.isLoading
                ? () => onPriceClicked(row.kpis)
                : undefined}
        >
            {row.isLoading ? '...' : column.formatValue(row)}
        </td>
    );
}

function loadVisibleColumnIds(): ManagementViewColumnId[] {
    const savedColumnIds = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (!savedColumnIds) return MANAGEMENT_VIEW_DEFAULT_COLUMN_IDS;

    try {
        const parsedColumnIds = JSON.parse(savedColumnIds);
        if (!Array.isArray(parsedColumnIds)) return MANAGEMENT_VIEW_DEFAULT_COLUMN_IDS;

        const validColumnIds = MANAGEMENT_VIEW_COLUMN_IDS
            .filter(columnId => parsedColumnIds.includes(columnId));
        return validColumnIds.length > 0
            ? normalizeManagementViewColumnIds(validColumnIds)
            : MANAGEMENT_VIEW_DEFAULT_COLUMN_IDS;
    } catch {
        return MANAGEMENT_VIEW_DEFAULT_COLUMN_IDS;
    }
}
